import * as footerStyles from '../../styles/layout/footer.module.scss';
import IconContainer from '../shared/IconContainer';
import {toExternalUrl} from '../../utils/utils';
import AppButton from '../shared/AppButton';
import Image from '../shared/Image';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'gatsby';

const Footer = React.memo(function Footer ({footer}) {
  const {t} = useTranslation();
  const footerLogo = footer?.logo;
  const copyRightDate = (new Date()).getFullYear();

  return (
    <>
      <div className={footerStyles.footerContainer}>
        <div className={footerStyles.mainContainer}>
          <div className={footerStyles.contactContainer}>
            <div className={footerStyles.logoContainer}>
              {footerLogo && (
                <Link
                  aria-label={t('footer.logos.main.arialabel', {website: 'Sciences Po'})}
                  data-cy="logo-footer"
                  to={footerLogo.navigationPath}
                >
                  <Image
                    aria-label={t('header.logo')}
                    className={footerStyles.logoImg}
                    image={footerLogo}
                  />
                </Link>
              )}
            </div>
            <div className={footerStyles.addressContainer}>
              <p className={footerStyles.contactTitle}>
                {t('footer.address.and.phone')}
              </p>
              <p className={footerStyles.address}>
                {footer?.address || '27, rue Saint Guillaume - 75007 Paris Cedex 07'}
              </p>
              <p className={footerStyles.phone}>
                {t('footer.phone.abbreviation')} {footer?.phone} {footer?.fax ? `| ${footer?.fax}` : ''}
              </p>
            </div>
          </div>
          <div className={footerStyles.mediaContainer}>
            {footer?.SocialNetworks?.length > 0 && (
              <div className={footerStyles.socialMediaContainer}>
                <p className={footerStyles.socialMediaTitle}>
                  {t('footer.follow.us')}
                </p>
                <div className={footerStyles.socialMediaIconContainer}>
                  <ul className={footerStyles.socialMediaList}>
                    {footer?.SocialNetworks?.map((network, index) => (
                      <li className={footerStyles.socialMediaItem}
                        key={index}
                      >
                        <a
                          aria-label={network.type + t('footer.social.networks')}
                          className={footerStyles.socialMediaAnchor}
                          href={network.link}
                          rel="noreferrer"
                          target="_blank"
                          title={network.type + t('footer.social.networks')}
                        >
                          <IconContainer color="WHITE"
                            icon={network.type}
                            size={25}
                          />
                        </a>
                      </li>))
                    }
                  </ul>
                </div>
              </div>
            )}
            <div className={footerStyles.newsletterContainer}>
              <p className={footerStyles.newsletterTitle}>
                {t('footer.newsletters.subscription')}
              </p>
              <div className={footerStyles.newsletterButtonContainer}>
                <AppButton
                  color="secondary"
                  label={footer?.NewsletterSubscription?.label}
                  noBorder
                  onClick={() =>
                    typeof window !== 'undefined' &&
                  window.open(toExternalUrl(footer?.NewsletterSubscription?.link), '_blank')
                  }
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      typeof window !== 'undefined' &&
                    window.open(toExternalUrl(footer?.NewsletterSubscription?.link), '_blank');
                    }
                  }}
                  padding={10}
                  tabIndex="0"
                  variant="contained"
                  width="80%"
                >
                  <p className={footerStyles.smartNewsletterButtonTitle}>
                    {footer?.NewsletterSubscription?.label}
                  </p>
                </AppButton>
              </div>
            </div>
          </div>
          <div className={footerStyles.miscellaneousContainer}>
            {footer?.group_of_additional_links?.map((group, index) => (
              <div className={footerStyles.linkBlocContainer}
                key={index}
              >
                <div className={footerStyles.linkBlocContent}>
                  <p className={footerStyles.linkBlocTitle}>
                    {group.title}
                  </p>
                  <ul>
                    {group?.links?.map((link, subindex) => {
                      if (link.label && link.URI) {
                        return (
                          <li className={footerStyles.linkItem}
                            key={`${index}-${subindex}`}
                          >
                            <div className={footerStyles.linkContainer}>
                              <a aria-label={link.label}
                                className={footerStyles.link}
                                href={toExternalUrl(link.URI)}
                                title={link.alternativeTest}
                              >
                                {link.label}
                              </a>
                            </div>
                          </li>);
                      }
                      return <Fragment key={`${index}-${subindex}`}/>;
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={footerStyles.copyrightContainer}>
        <p className={footerStyles.copyright}>
          ©{t('footer.author', {date: copyRightDate})}
        </p>
      </div>
    </>);
});

Footer.propTypes = {
  footer: PropTypes.shape({
    logo: PropTypes.shape({
      navigationPath: PropTypes.string,
      image: PropTypes.object,
    }),
    address: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    SocialNetworks: PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string,
        type: PropTypes.string,
      })
    ),
    NewsletterSubscription: PropTypes.shape({
      label: PropTypes.string,
      link: PropTypes.string,
    }),
    group_of_additional_links: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        links: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            URI: PropTypes.string,
            alternativeTest: PropTypes.string,
          })
        ),
      })
    ),
  }),
  locale: PropTypes.string,
};

export default Footer;
